import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Clock, CheckCircle, XCircle } from "lucide-react";
import { CryptoIcon } from "../components/icons/CryptoIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const USDT_NETWORKS = [
  { id: "TRC20", label: "USDT (TRC-20)", network: "Tron", fee: "1 USDT", time: "~1-3 мин" },
  { id: "BEP20", label: "USDT (BEP-20)", network: "BSC", fee: "0.3 USDT", time: "~3 мин" },
  { id: "ERC20", label: "USDT (ERC-20)", network: "Ethereum", fee: "3-10 USDT", time: "~5 мин" },
  { id: "TON", label: "USDT (TON)", network: "TON", fee: "0 USDT", time: "~1 мин" },
  { id: "SOL", label: "USDT (Solana)", network: "Solana", fee: "1 USDT", time: "~1 мин" },
  { id: "MATIC", label: "USDT (Polygon)", network: "Polygon", fee: "1 USDT", time: "~3 мин" },
  { id: "ARB", label: "USDT (Arbitrum)", network: "Arbitrum One", fee: "1 USDT", time: "~1 мин" },
];

const ASSETS = [
  { code: "USDT", name: "Tether USD", min: 10, decimals: 2 },
  { code: "TON", name: "Toncoin", min: 0.1, decimals: 4, network: "TON Network", fee: "0.01 TON", time: "~1 мин" },
  { code: "TRX", name: "Tron", min: 1, decimals: 4, network: "Tron", fee: "1 TRX", time: "~1 мин" },
  { code: "BNB", name: "BNB", min: 0.001, decimals: 6, network: "BNB Smart Chain", fee: "0.0005 BNB", time: "~3 мин" },
];

const Withdrawal = () => {
  const { user, token, refreshUser } = useAuth();
  const [activeAsset, setActiveAsset] = useState("USDT");
  const [usdtNetworkIdx, setUsdtNetworkIdx] = useState(0);
  const [walletAddress, setWalletAddress] = useState("");
  const [amount, setAmount] = useState("");
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(false);

  const asset = useMemo(() => ASSETS.find((a) => a.code === activeAsset) || ASSETS[0], [activeAsset]);
  const usdtNetwork = USDT_NETWORKS[usdtNetworkIdx];

  const userAssets = user?.assets || {};
  const usdtBalance = user?.balance || 0;
  const availableBalance = activeAsset === "USDT" ? usdtBalance : Number(userAssets[activeAsset] || 0);
  const totalBalance = usdtBalance + (user?.trading_balance || 0);

  useEffect(() => {
    fetchWithdrawals();
  }, [token]); // eslint-disable-line

  const fetchWithdrawals = async () => {
    try {
      const response = await axios.get(`${API}/withdrawals`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setWithdrawals(response.data);
    } catch (error) {
      console.error("Error fetching withdrawals:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!walletAddress || !amount) {
      toast.error("Заполните все поля");
      return;
    }
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount <= 0) {
      toast.error("Сумма должна быть больше 0");
      return;
    }
    if (numAmount < asset.min) {
      toast.error(`Минимальная сумма: ${asset.min} ${asset.code}`);
      return;
    }
    if (numAmount > availableBalance) {
      toast.error("Недостаточно средств");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        wallet_address: walletAddress,
        amount: numAmount,
        asset_kind: asset.code,
        asset: asset.code === "USDT" ? usdtNetwork.label : asset.code,
      };
      await axios.post(`${API}/withdrawals`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Заявка на вывод создана");
      setWalletAddress("");
      setAmount("");
      fetchWithdrawals();
      refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ошибка при создании заявки");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return (
          <span className="status-pending inline-flex items-center gap-1">
            <Clock size={14} /> В обработке
          </span>
        );
      case "completed":
      case "approved":
        return (
          <span className="status-completed inline-flex items-center gap-1">
            <CheckCircle size={14} /> Выполнено
          </span>
        );
      case "rejected":
        return (
          <span className="status-rejected inline-flex items-center gap-1">
            <XCircle size={14} /> Отклонено
          </span>
        );
      default:
        return status;
    }
  };

  return (
    <div className="min-h-screen bybit-grid-subtle">
      <header className="border-b border-[#1F1F1F] bg-[#000000]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/wallet" className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors">
              <ArrowLeft size={20} />
              <span>Назад</span>
            </Link>
            <h1 className="font-chivo font-bold text-xl text-white">Web-<span className="text-[#EDEDED]">3</span></h1>
            <div className="flex items-center gap-2 bg-[#0A0A0A] px-4 py-2 rounded border border-[#1F1F1F]">
              <span className="text-[#A1A1AA] text-sm">Общий:</span>
              <span className="font-mono text-white font-bold">$ {totalBalance.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] p-6 mb-8">
          <h2 className="font-chivo font-bold text-xl text-white mb-2">Вывод средств</h2>
          <p className="text-[#A1A1AA] text-sm mb-6">
            Выберите актив для вывода. Каждый актив выводится отдельно.
          </p>

          {/* Asset Selector */}
          <div className="mb-5">
            <label className="block text-[#A1A1AA] text-sm mb-2">Актив</label>
            <div className="grid grid-cols-4 gap-2">
              {ASSETS.map((a) => {
                const active = a.code === activeAsset;
                const balance = a.code === "USDT" ? usdtBalance : Number(userAssets[a.code] || 0);
                return (
                  <button
                    key={a.code}
                    type="button"
                    onClick={() => {
                      setActiveAsset(a.code);
                      setAmount("");
                      setWalletAddress("");
                    }}
                    className={`flex flex-col items-center gap-1.5 px-3 py-3 rounded border transition-colors ${
                      active
                        ? "border-white bg-white/10"
                        : "border-[#1F1F1F] bg-[#000000] hover:border-[#2E2E2E]/40"
                    }`}
                    data-testid={`withdraw-asset-${a.code}-btn`}
                  >
                    <CryptoIcon code={a.code} size={28} />
                    <span className={`text-xs font-semibold ${active ? "text-white" : "text-[#A1A1AA]"}`}>
                      {a.code}
                    </span>
                    <span className="text-[10px] text-[#71717A] font-mono">
                      {balance.toFixed(a.code === "USDT" ? 2 : 4)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Available balance */}
          <div className="bg-[#000000] border border-[#1F1F1F] rounded p-3 mb-5 flex items-center justify-between">
            <span className="text-[#A1A1AA] text-sm">Доступно к выводу</span>
            <span className="font-mono text-white font-bold">
              {availableBalance.toFixed(asset.decimals)} {asset.code}
            </span>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Network select — only for USDT (multi-chain) */}
            {asset.code === "USDT" && (
              <div>
                <label className="block text-[#A1A1AA] text-sm mb-2">Сеть</label>
                <div className="grid grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                  {USDT_NETWORKS.map((n, idx) => {
                    const active = idx === usdtNetworkIdx;
                    return (
                      <button
                        key={n.id}
                        type="button"
                        onClick={() => setUsdtNetworkIdx(idx)}
                        className={`flex flex-col items-start px-3 py-2.5 rounded border text-left transition-colors ${
                          active
                            ? "border-white bg-white/10"
                            : "border-[#1F1F1F] bg-[#000000] hover:border-[#2E2E2E]/40"
                        }`}
                        data-testid={`withdraw-network-${n.id}-btn`}
                      >
                        <span className="text-white text-sm font-semibold">{n.label}</span>
                        <span className="text-[#71717A] text-xs">
                          {n.network} • Комиссия {n.fee} • {n.time}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Native asset network info */}
            {asset.code !== "USDT" && (
              <div className="bg-[#000000] border border-[#1F1F1F] rounded p-3 text-sm">
                <div className="text-[#A1A1AA]">Сеть: <span className="text-white">{asset.network}</span></div>
                <div className="text-[#A1A1AA] mt-0.5">
                  Комиссия: <span className="text-white">{asset.fee}</span> • Время: <span className="text-white">{asset.time}</span>
                </div>
              </div>
            )}

            {/* Wallet address */}
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2">Адрес кошелька</label>
              <input
                type="text"
                value={walletAddress}
                onChange={(e) => setWalletAddress(e.target.value)}
                placeholder={`Введите ${asset.code} адрес`}
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#71717A] focus:border-white focus:outline-none font-mono text-sm"
                data-testid="wallet-address-input"
              />
            </div>

            {/* Amount */}
            <div>
              <label className="block text-[#A1A1AA] text-sm mb-2 flex items-center justify-between">
                <span>Сумма ({asset.code})</span>
                <button
                  type="button"
                  onClick={() => setAmount(String(availableBalance))}
                  className="text-[#EDEDED] hover:underline text-xs"
                  data-testid="max-btn"
                >
                  Макс.
                </button>
              </label>
              <input
                type="number"
                step="any"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder={`Мин. ${asset.min} ${asset.code}`}
                className="w-full bg-[#000000] border border-[#1F1F1F] rounded px-4 py-3 text-white placeholder-[#71717A] focus:border-white focus:outline-none font-mono"
                data-testid="amount-input"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !walletAddress || !amount}
              className={`w-full py-3 font-bold rounded transition-colors ${
                walletAddress && amount && parseFloat(amount) > 0
                  ? "bg-[#FF8A1E] hover:bg-[#E67A00] text-white"
                  : "bg-[#1F1F1F] text-[#71717A] cursor-not-allowed"
              } disabled:opacity-50`}
              data-testid="submit-withdrawal-btn"
            >
              {loading ? "Обработка..." : `Запросить вывод ${asset.code}`}
            </button>
          </form>
        </div>

        {/* History */}
        <div className="bg-[#0A0A0A] rounded-xl border border-[#1F1F1F] overflow-hidden">
          <div className="p-4 border-b border-[#1F1F1F]">
            <h3 className="font-chivo font-bold text-white">История выводов</h3>
          </div>
          {/* Mobile cards */}
          <div className="lg:hidden divide-y divide-[#1F1F1F]">
            {withdrawals.length === 0 ? (
              <div className="text-center py-12 text-[#A1A1AA]">Нет заявок на вывод</div>
            ) : (
              withdrawals.map((w) => {
                const date = new Date(w.created_at);
                const fmt = date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
                const kind = (w.asset_kind || "USDT").toUpperCase();
                const decimals = kind === "USDT" ? 2 : 4;
                return (
                  <div key={w.id} className="p-4" data-testid={`withdrawal-card-${w.id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <CryptoIcon code={kind} size={20} />
                        <span className="text-white text-sm">{w.asset}</span>
                      </div>
                      {getStatusBadge(w.status)}
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[#F87171] text-sm">-{Number(w.amount).toFixed(decimals)} {kind}</span>
                      <span className="text-[#71717A] text-xs font-mono">{fmt}</span>
                    </div>
                    <code className="mt-2 block bg-[#000000] text-[#A1A1AA] px-2 py-1 rounded text-xs font-mono break-all">
                      {w.wallet_address}
                    </code>
                  </div>
                );
              })
            )}
          </div>
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full" data-testid="withdrawals-table">
              <thead>
                <tr className="border-b border-[#1F1F1F]">
                  <th className="text-left text-[#A1A1AA] text-xs uppercase tracking-wider font-semibold py-3 px-4">Дата</th>
                  <th className="text-left text-[#A1A1AA] text-xs uppercase tracking-wider font-semibold py-3 px-4">Актив</th>
                  <th className="text-right text-[#A1A1AA] text-xs uppercase tracking-wider font-semibold py-3 px-4">Сумма</th>
                  <th className="text-left text-[#A1A1AA] text-xs uppercase tracking-wider font-semibold py-3 px-4">Адрес</th>
                  <th className="text-left text-[#A1A1AA] text-xs uppercase tracking-wider font-semibold py-3 px-4">Статус</th>
                </tr>
              </thead>
              <tbody>
                {withdrawals.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center py-12 text-[#A1A1AA]">
                      Нет заявок на вывод
                    </td>
                  </tr>
                ) : (
                  withdrawals.map((w) => {
                    const date = new Date(w.created_at);
                    const fmtDate = date.toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
                    const fmtTime = date.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
                    const kind = (w.asset_kind || "USDT").toUpperCase();
                    const decimals = kind === "USDT" ? 2 : 4;
                    return (
                      <tr key={w.id} className="border-b border-[#1F1F1F] hover:bg-[#000000]/50 transition-colors">
                        <td className="py-3 px-4">
                          <div className="text-white text-sm">{fmtDate}</div>
                          <div className="text-[#71717A] text-xs">{fmtTime}</div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <CryptoIcon code={kind} size={20} />
                            <span className="text-white text-sm">{w.asset}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 font-mono text-[#F87171] text-right">
                          -{Number(w.amount).toFixed(decimals)} {kind}
                        </td>
                        <td className="py-3 px-4">
                          <code className="bg-[#000000] text-[#A1A1AA] px-2 py-1 rounded text-xs font-mono break-all max-w-[200px] block">
                            {w.wallet_address}
                          </code>
                        </td>
                        <td className="py-3 px-4">{getStatusBadge(w.status)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Withdrawal;
